import React, { useState } from "react";
import "./FooterComponent.scss";
import {
  FaInstagram,
  FaFacebook,
  FaWhatsapp,
  FaEnvelope,
  FaTimes,
} from "react-icons/fa";

const UPDATED_DATE = "03 May 2026";

const links = [
  { label: "Home", id: "home" },
  { label: "FAQ", id: "faq" },
  { label: "Help", id: "help" },
];

const policyLinks = [
  { label: "Refund Policy", id: "refund" },
  { label: "Privacy Policy", id: "privacy" },
  { label: "Terms & Conditions", id: "terms" },
];

const policyContent = {
  refund: {
    title: "Refund Policy",
    content: (
      <>
        <p>
          Thank you for choosing Plan My Guest. Plan My Guest provides digital
          wedding planning services, including personalized wedding invitation
          websites, guest management tools, and related services.
        </p>

        <h3>No Refund Policy</h3>
        <p>
          All payments made to Plan My Guest are non-refundable. Once a purchase
          is completed, no refunds, returns, or cancellations will be provided.
        </p>

        <h3>Service Nature</h3>
        <p>
          Our services are digital, customized, and may be instantly accessible
          after purchase. Therefore, they cannot be returned once activated or
          delivered.
        </p>

        <h3>Technical Issues</h3>
        <p>
          If you face any technical issue, our team will make every effort to
          resolve it as soon as possible. However, technical issues do not
          qualify for refunds.
        </p>
      </>
    ),
  },

  privacy: {
    title: "Privacy Policy",
    content: (
      <>
        <p>
          At Plan My Guest, we are committed to protecting your privacy and
          safeguarding your personal information.
        </p>

        <h3>Information We Collect</h3>
        <p>
          We may collect personal details such as your name, phone number, email
          address, wedding details, guest information, account details, and
          payment-related information processed through secure third-party
          payment providers.
        </p>

        <h3>How We Use Your Information</h3>
        <p>
          We use your information to provide our wedding planning services,
          create digital invitation websites, manage guest lists, track RSVPs,
          provide customer support, improve our platform, and maintain security.
        </p>

        <h3>Information Sharing</h3>
        <p>
          We do not sell or rent your personal data. We may only share necessary
          information with trusted service providers or legal authorities when
          required by law.
        </p>

        <h3>Data Security</h3>
        <p>
          We take reasonable steps to protect your information. However, no
          online system can be guaranteed to be completely secure.
        </p>

        <h3>Your Rights</h3>
        <p>
          You may contact us to request access, correction, or deletion of your
          personal data where applicable.
        </p>
      </>
    ),
  },

  terms: {
    title: "Terms & Conditions",
    content: (
      <>
        <p>
          Welcome to Plan My Guest. By accessing or using our platform, you
          agree to comply with these Terms and Conditions.
        </p>

        <h3>Services Provided</h3>
        <p>
          Plan My Guest provides digital wedding planning services including
          digital invitation websites, RSVP tracking, guest list management,
          seating arrangement tools, budget planning, QR code check-in, and
          related services.
        </p>

        <h3>Payments</h3>
        <p>
          All payments must be made in full before premium features or services
          are activated. Payments may be processed through trusted third-party
          payment providers such as PayHere.
        </p>

        <h3>Refunds</h3>
        <p>
          All payments are non-refundable. Please refer to our Refund Policy for
          more details.
        </p>

        <h3>User Content</h3>
        <p>
          You are responsible for the accuracy of content you provide, including
          names, photos, guest information, event details, and other uploaded
          materials.
        </p>

        <h3>Service Availability</h3>
        <p>
          We aim to provide a reliable service, but we do not guarantee
          uninterrupted access. Temporary downtime may occur due to maintenance
          or technical issues.
        </p>

        <h3>Limitation of Liability</h3>
        <p>
          Plan My Guest shall not be liable for indirect losses, event
          disruptions, loss of data, or issues caused by third-party services.
        </p>

        <h3>Governing Law</h3>
        <p>These Terms shall be governed by the laws of Sri Lanka.</p>
      </>
    ),
  },
};

const FooterComponent = () => {
  const [activePolicy, setActivePolicy] = useState(null);

  const onClick = (e, id) => {
    e.preventDefault();

    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (id === "faq") {
      window.location.href = "/faq";
      return;
    }

    if (id === "help") {
      window.location.href = "/help";
      return;
    }
  };

  const openPolicy = (e, id) => {
    e.preventDefault();
    setActivePolicy(id);
    document.body.classList.add("policy-modal-open");
  };

  const closePolicy = () => {
    setActivePolicy(null);
    document.body.classList.remove("policy-modal-open");
  };

  const currentPolicy = activePolicy ? policyContent[activePolicy] : null;

  return (
    <>
      <footer className="footer" aria-label="Site footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <span className="footer__logo">Plan My Guest</span>
            <span className="footer__copy">
              Copyright © 2026 Plan My Guest
            </span>
          </div>

          <div className="footer__center">
            <nav className="footer__nav" aria-label="Quick links">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.id === "home" ? "#home" : `/${l.id}`}
                  className="footer__link"
                  onClick={(e) => onClick(e, l.id)}
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <nav className="footer__policies" aria-label="Policy links">
              {policyLinks.map((p) => (
                <a
                  key={p.id}
                  href={`#${p.id}`}
                  className="footer__policy-link"
                  onClick={(e) => openPolicy(e, p.id)}
                >
                  {p.label}
                </a>
              ))}
            </nav>
          </div>

          <div className="footer__social" aria-label="Social media">
            <a
              className="social__btn"
              href="https://wa.me/94705367980"
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp"
            >
              <FaWhatsapp />
            </a>

            <a
              className="social__btn"
              href="mailto:planmyguestbookingso@gmail.com"
              aria-label="Email"
            >
              <FaEnvelope />
            </a>

            <a
              className="social__btn"
              href="https://www.instagram.com/plan_my_guest?igsh=MXRramxqcmpsMGJxZw=="
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram />
            </a>

            <a
              className="social__btn"
              href="https://www.facebook.com/share/1BTSuupVzm/?mibextid=wwXIfr"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
            >
              <FaFacebook />
            </a>
          </div>
        </div>
      </footer>

      {currentPolicy && (
        <div className="policy-modal" role="dialog" aria-modal="true">
          <div className="policy-modal__overlay" onClick={closePolicy} />

          <div className="policy-modal__box">
            <button
              type="button"
              className="policy-modal__close"
              onClick={closePolicy}
              aria-label="Close policy popup"
            >
              <FaTimes />
            </button>

            <div className="policy-modal__header">
              <h2>{currentPolicy.title}</h2>
              <p>Last Updated: {UPDATED_DATE}</p>
            </div>

            <div className="policy-modal__content">
              {currentPolicy.content}

              <h3>Contact Us</h3>
              <p>
                Phone: 070 536 7980
                <br />
                Website: www.planmyguest.com
                <br />
                Email: planmyguestbookingso@gmail.com
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FooterComponent;